"use client";

import { Add2, Remove } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import { cva, type VariantProps } from "class-variance-authority";
import { type ReactNode, useId, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Typography } from "@/components/ui/Typography";

type AccordionSize = NonNullable<VariantProps<typeof itemVariants>["size"]>;

export interface AccordionItem {
	title: string;
	content?: ReactNode;
	/** Stable identity across reorders. Falls back to `${title}-${index}`. */
	id?: string;
}

export interface AccordionProps {
	items: AccordionItem[];
	size?: AccordionSize;
	allowMultiple?: boolean;
	defaultOpenIndex?: number | number[];
	className?: string;
	darkMode?: boolean;
}

const itemVariants = cva("flex w-full border-b border-border-medium", {
	variants: {
		size: {
			large: "",
			small: "",
		},
	},
	defaultVariants: { size: "large" },
});

const triggerVariants = cva(
	"flex w-full cursor-pointer items-center gap-grid-component-spacing-2xs text-left",
	{
		variants: {
			size: {
				large: "py-grid-component-spacing-md",
				small: "py-grid-component-spacing-xs",
			},
		},
		defaultVariants: { size: "large" },
	},
);

const titleVariants = cva(
	"flex-[1_0_0] min-w-px text-text-dark-text-1 group-hover:underline underline-offset-[15%]",
	{
		variants: {
			size: {
				large: "",
				small: "",
			},
		},
		defaultVariants: { size: "large" },
	},
);

const iconVariants = cva("shrink-0 text-text-dark-text-1", {
	variants: {
		size: {
			large: "size-5",
			small: "size-4",
		},
	},
	defaultVariants: { size: "large" },
});

const panelVariants = cva("grid overflow-hidden transition-[grid-template-rows] duration-300", {
	variants: {
		isOpen: {
			true: "grid-rows-[1fr]",
			false: "grid-rows-[0fr]",
		},
	},
});

const contentPaddingVariants = cva("", {
	variants: {
		size: {
			large: "pb-grid-component-spacing-md",
			small: "pb-grid-component-spacing-xs",
		},
	},
	defaultVariants: { size: "large" },
});

const toIndexSet = (value: number | number[] | undefined) => {
	if (value === undefined) {
		return new Set<number>();
	}
	return new Set(Array.isArray(value) ? value : [value]);
};

export const Accordion = ({
	items,
	size = "large",
	allowMultiple = false,
	defaultOpenIndex,
	className,
	darkMode,
}: AccordionProps) => {
	const [openIndexes, setOpenIndexes] = useState(() => toIndexSet(defaultOpenIndex));
	const baseId = useId();

	const toggle = (index: number) => {
		setOpenIndexes((prev) => {
			const next = new Set(allowMultiple ? prev : []);
			if (prev.has(index)) {
				next.delete(index);
			} else {
				next.add(index);
			}
			return next;
		});
	};

	const titleVariant = size === "small" ? "label-sm-reg" : "label-md-reg";
	const bodyVariant = size === "small" ? "body-xs-reg" : "body-sm-reg";

	return (
		<div className={twMerge("flex flex-col", className)}>
			{items.map((item, index) => {
				const isOpen = openIndexes.has(index);
				const panelId = `${baseId}-panel-${index}`;
				const triggerId = `${baseId}-trigger-${index}`;
				const Icon = isOpen ? Remove : Add2;

				return (
					<div key={item.id ?? `${item.title}-${index}`} className={itemVariants({ size })}>
						<div className="flex w-full flex-col">
							<button
								type="button"
								id={triggerId}
								onClick={() => toggle(index)}
								aria-expanded={isOpen}
								aria-controls={panelId}
								className={`group ${triggerVariants({ size })}`}
							>
								<Typography
									as="span"
									variant={titleVariant}
									className={clsx(titleVariants({ size }), darkMode && "text-text-light-text-1")}
								>
									{item.title}
								</Typography>
								<Icon
									className={clsx(iconVariants({ size }), darkMode && "text-text-light-text-1")}
								/>
							</button>
							{item.content !== undefined && item.content !== null && (
								<section
									id={panelId}
									aria-labelledby={triggerId}
									className={panelVariants({ isOpen })}
									aria-hidden={!isOpen}
								>
									<div
										className={clsx(
											"min-h-0 overflow-hidden text-text-dark-text-1",
											darkMode && "text-text-light-text-1",
										)}
									>
										<div className={contentPaddingVariants({ size })}>
											{typeof item.content === "string" ? (
												<Typography variant={bodyVariant}>{item.content}</Typography>
											) : (
												item.content
											)}
										</div>
									</div>
								</section>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

Accordion.displayName = "Accordion";
